import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, CheckCircle2, ListChecks } from 'lucide-react';
import { API_BASE } from '../../config/api';
import { activityMeta, activityLaunchPath } from '../../lib/activityTypes';

const API = `${API_BASE}/api`;

// "Up next" prompt shown after finishing a programme activity.
export default function NextActivityCard({ programmeId, activityId, token }) {
  const navigate = useNavigate();
  const [next, setNext] = useState(undefined);

  useEffect(() => {
    if (!programmeId || !token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/programmes/${programmeId}`, { headers }),
      axios.get(`${API}/programmes/${programmeId}/my-progress`, { headers }).catch(() => ({ data: null })),
    ]).then(([p, prog]) => {
      const done = new Set(prog.data?.completed_activity_ids || []);
      const list = (p.data.periods || []).flatMap((per) => (per.activities || []).filter((a) => a.status !== 'draft').map((a) => ({ ...a, period_title: per.title, period_locked: per.locked })));
      const idx = list.findIndex((a) => a.activity_id === activityId);
      const after = list.slice(idx + 1).concat(idx >= 0 ? list.slice(0, idx) : []);
      setNext(after.find((a) => !done.has(a.activity_id) && !a.period_locked && !a.locked) || null);
    }).catch(() => setNext(null));
  }, [programmeId, activityId, token]);

  if (!programmeId || next === undefined) return null;
  const back = () => navigate(`/programmes/${programmeId}`);
  if (!next) {
    return (
      <div className="mt-8 rounded-2xl border border-teal-500/30 bg-teal-500/10 p-5 text-left" data-testid="next-activity-none">
        <p className="text-teal-200 text-sm flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />You&apos;re all caught up — no more unlocked activities right now.</p>
        <button type="button" onClick={back} className="mt-3 text-sm text-teal-300 hover:underline" data-testid="next-activity-back-btn">Back to programme</button>
      </div>
    );
  }
  const meta = activityMeta(next);
  const Icon = meta.icon;
  const path = activityLaunchPath(next, programmeId);
  return (
    <div className="mt-8 rounded-2xl border border-zinc-700 bg-zinc-900 p-5 text-left" data-testid="next-activity-card">
      <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-3">Up next</p>
      <div className="flex flex-wrap items-center gap-4">
        <span className="h-12 w-12 shrink-0 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${meta.color}1f`, color: meta.color }}><Icon className="h-6 w-6" /></span>
        <div className="flex-1 min-w-[180px]">
          <p className="text-xs" style={{ color: meta.color }}>{next.period_title} · {meta.label}</p>
          <p className="text-white font-semibold" data-testid="next-activity-title">{next.title}</p>
        </div>
        <button type="button" onClick={() => navigate(path || `/programmes/${programmeId}`)}
          className="group flex items-center gap-2 px-5 py-2.5 rounded-full bg-teal-600 hover:bg-teal-500 text-white font-medium transition-colors" data-testid="next-activity-start-btn">
          Start next <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </button>
      </div>
      <button type="button" onClick={back} className="mt-4 flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="next-activity-back-btn">
        <ListChecks className="h-4 w-4" />Back to programme
      </button>
    </div>
  );
}
